// scheduling/backend-actions-client.ts
import type {
    BackendActionsClient,
    HasSendAttemptResponse,
    AcquireSlotResponse,
    DeleteAndLogResponse,
    CheckEngineCmdLockResponse,
    ClientClock,
    SendProtocolUserMessage,
} from './send-protocol.types'
import { senderClaimFields } from '../engine/sender-binding'

// Must list EVERY reason acquire-send-slot can return. A missing entry is not a no-op: the reason is
// silently rewritten to `granted`/`backoff`, and the orchestrator then takes the wrong branch for it.
// `gateway_preferred` was missing (#172) — so the BE#167 expected-wait early-return never fired, and
// every grace-window deny ran a store-check and wrote a row blaming a contested lock.
const SLOT_REASONS: ReadonlyArray<AcquireSlotResponse['reason']> = [
    'granted', 'backoff', 'exhausted', 'window_expired', 'not_found', 'already_terminal',
    'gateway_preferred', 'not_yet_due', 'not_my_sender',
]
const SLOT_REASON_STRINGS: readonly string[] = SLOT_REASONS
function isSlotReason(v: unknown): v is AcquireSlotResponse['reason'] {
    return typeof v === 'string' && SLOT_REASON_STRINGS.includes(v)
}
function coerceSlotReason(v: unknown, allowToSend: boolean): AcquireSlotResponse['reason'] {
    return isSlotReason(v) ? v : (allowToSend ? 'granted' : 'backoff')
}

/** Caller-injected HTTP POST. Cross-runtime — pod wires axios; extension
 *  wires its `apiCall` helper. The orchestrator never sees this — it talks
 *  to the BackendActionsClient interface only. */
export type BackendActionsPost = (path: string, body: unknown) => Promise<unknown>

// ── narrowing the untyped HTTP body ─────────────────────────────────────────
type Body = { readonly [key: string]: unknown }
function isBody(v: unknown): v is Body {
    return typeof v === 'object' && v !== null && !Array.isArray(v)
}
const asBody = (v: unknown): Body | null => (isBody(v) ? v : null)
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined)
const finiteNum = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
/** Shape check on the doc the server hands back. `_id` is required and `retryCount`, when present, must be
 *  a number (the protocol does arithmetic on it); the engine reads its send content (message/asset/poll)
 *  off the same object, which passes through unchanged. */
function isSendProtocolUserMessage(v: unknown): v is SendProtocolUserMessage {
    const b = asBody(v)
    return b !== null && typeof b._id === 'string' && (b.retryCount === undefined || typeof b.retryCount === 'number')
}
const userMessageOf = (v: unknown): SendProtocolUserMessage | undefined => (isSendProtocolUserMessage(v) ? v : undefined)

/** The browser/Node system clock as a ClientClock reading. */
export function systemClientClock(): ClientClock {
    const nowMs = Date.now()
    // getTimezoneOffset() is minutes WEST of UTC (Sydney in summer = -660); we send minutes EAST.
    return { nowMs, tzOffsetMin: -new Date(nowMs).getTimezoneOffset() + 0 }
}

export interface CreateBackendActionsClientOptions {
    post: BackendActionsPost
    /** FE#447: when wired, EVERY slot / can-i-send request carries the client's own clock reading
     *  (`clientNowMs`, `clientTzOffsetMin`), sampled at the moment of the request. Opt-in so a pod (whose
     *  clock is the server's) and existing callers send exactly what they always did. */
    clock?: () => ClientClock
    log?: (level: 'warn' | 'debug', msg: string, extra?: unknown) => void
}

/** The concrete client always wires `acquireSendSlot`; only older engine bundles lack it (hence optional
 *  on the interface). Saying so in the return type spares every caller a `!`. */
export type ConcreteBackendActionsClient = BackendActionsClient & Required<Pick<BackendActionsClient, 'acquireSendSlot'>>

export function createBackendActionsClient(opts: CreateBackendActionsClientOptions): ConcreteBackendActionsClient {
    const { post, log, clock } = opts
    /** One fresh reading, as body fields — or nothing at all: a missing/broken/non-finite clock must
     *  never cost a send, the server simply falls back to its own time. */
    const clockFields = (): { clientNowMs?: number; clientTzOffsetMin?: number } => {
        if (!clock) return {}
        try {
            const c = clock()
            if (!Number.isFinite(c.nowMs)) return {}
            return Number.isFinite(c.tzOffsetMin)
                ? { clientNowMs: c.nowMs, clientTzOffsetMin: c.tzOffsetMin }
                : { clientNowMs: c.nowMs }
        } catch {
            return {}
        }
    }
    return {
        async hasSendAttemptBeenMadeToMessage(args): Promise<HasSendAttemptResponse | undefined> {
            try {
                const raw = await post('/actions?cmd=has-send-attempt-been-made-to-message', {
                    messageId: args.messageId,
                    retryAttemptNumber: args.retryAttemptNumber,
                    isSendMessageNow: args.isSendMessageNow,
                    ...senderClaimFields(args),
                    ...clockFields(),
                })
                const resp = asBody(raw)
                if (!resp) return undefined
                const userMessage = userMessageOf(resp.userMessage)
                const error = str(resp.error)
                return {
                    allowToSend: !!resp.allowToSend,
                    initialAttemptLockExists: !!resp.initialAttemptLockExists,
                    currentAttemptCount: typeof resp.currentAttemptCount === 'number' ? resp.currentAttemptCount : 0,
                    wasSendAttempted: !!resp.wasSendAttempted,
                    ...(userMessage ? { userMessage } : {}),
                    ...(error !== undefined ? { error } : {}),
                    // #451: forward only a literal true; an absent/other value stays absent so an older server is unchanged.
                    ...(resp.attemptCountAuthoritative === true ? { attemptCountAuthoritative: true } : {}),
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] hasSendAttemptBeenMadeToMessage failed: ${msg}`)
                return undefined
            }
        },

        async acquireSendSlot(args): Promise<AcquireSlotResponse | undefined> {
            try {
                const raw = await post('/actions?cmd=acquire-send-slot', {
                    messageId: args.messageId,
                    isSendMessageNow: args.isSendMessageNow,
                    clientType: args.clientType,
                    extensionVersion: args.extensionVersion,
                    // Per-message send metadata — forwarded alongside extensionVersion.
                    isGroup: args.isGroup,
                    isCommunity: args.isCommunity,
                    isParentGroup: args.isParentGroup,
                    announce: args.announce,
                    groupType: args.groupType,
                    parentGroupId: args.parentGroupId,
                    messageType: args.messageType,
                    sendErrorReason: args.sendErrorReason,
                    recipientAddressing: args.recipientAddressing,
                    // #922 S0b: absent fields are dropped, so a pre-S0b caller's body is unchanged.
                    ...senderClaimFields(args),
                    ...clockFields(),
                })
                const resp = asBody(raw)
                if (!resp) return undefined
                const allowToSend = !!resp.allowToSend
                // Carry the full userMessage through (the engine reads send content off it), as
                // hasSendAttemptBeenMadeToMessage does: same object, shape-checked on `_id`.
                const userMessage = userMessageOf(resp.userMessage)
                const serverNowMs = finiteNum(resp.serverNowMs)
                const retryAfterMs = finiteNum(resp.retryAfterMs)
                return {
                    allowToSend,
                    attemptNumber: typeof resp.attemptNumber === 'number' ? resp.attemptNumber : 0,
                    reason: coerceSlotReason(resp.reason, allowToSend),
                    ...(userMessage ? { userMessage } : {}),
                    ...(serverNowMs !== undefined ? { serverNowMs } : {}),
                    // FE#447: on 'not_yet_due' the server says (on ITS timeline) when to come back.
                    ...(retryAfterMs !== undefined && retryAfterMs > 0 ? { retryAfterMs } : {}),
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] acquireSendSlot failed: ${msg}`)
                return undefined
            }
        },

        async createMessageAttemptLog(args): Promise<{ success: boolean; error?: string }> {
            try {
                const resp = asBody(await post('/actions?cmd=create-message-attempt-log', { ...args }))
                const error = str(resp?.error)
                return { success: !!resp?.success, ...(error !== undefined ? { error } : {}) }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] createMessageAttemptLog failed: ${msg}`)
                return { success: false, error: msg }
            }
        },

        async deleteUserMessageAndCreateLog(args): Promise<DeleteAndLogResponse> {
            // Intentionally NOT swallowed — caller (orchestrator) must observe
            // failure to avoid silently dropping sentCount writes.
            const resp = asBody(await post('/actions?cmd=delete-user-message-and-create-log', { ...args }))
            return {
                isLogCreated: !!resp?.isLogCreated,
                isMessageDeleted: !!resp?.isMessageDeleted,
                isRecurringScheduled: !!resp?.isRecurringScheduled,
                newMessage: resp?.newMessage,
                message: resp?.message,
            }
        },

        async checkEngineCmdLock(args): Promise<CheckEngineCmdLockResponse | undefined> {
            try {
                const body: Record<string, unknown> = { engineCmdId: args.engineCmdId }
                if (typeof args.ttlSec === 'number') body.ttlSec = args.ttlSec
                const resp = asBody(await post('/actions?cmd=check-engine-cmd-lock', body))
                if (!resp) return undefined
                return {
                    acquired: !!resp.acquired,
                    lockId: typeof resp.lockId === 'string' ? resp.lockId : args.engineCmdId,
                    acquired_at: typeof resp.acquired_at === 'string' ? resp.acquired_at : undefined,
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : 'unknown'
                log?.('warn', `[backend-actions] checkEngineCmdLock failed: ${msg}`)
                return undefined
            }
        },
    }
}
